import { DestroyRef, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  take,
  tap,
  timer,
} from 'rxjs';
import { IntegrationModel } from 'src/app/models/api';
import { PolicyMinimalModel } from 'src/app/models/api/governance/policy.model';
import { BaseViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { PoliciesHttpService } from '../../httpServices/governance/policiesHttpService/policies-http.service';
import { ViolationRulesHttpService } from '../../httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { HttpErrorHandlerService } from '../../httpServices/httpErrorHandlerService/http-error-handler.service';
import { IntegrationsHttpService } from '../../httpServices/integrationsHttpsService/integrations-http.service';
import { LabelModel } from 'src/app/models/api/common';
import { LabelsHttpService } from '../../httpServices/labelsHttpService/labels-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { Platform } from 'src/app/models/api';
import {
  EffectivePolicySettingEnum,
  CategoryToRules,
} from 'src/app/services/infra/store/model/effectivePolicySetting.model';
import { EffectivePolicySettingsStore } from 'src/app/services/infra/store/effectivePolicySettingStore';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { ViolationModel } from 'src/app/models/api/violations.model';
import { IDataLayerTableRowViewModel } from 'src/app/models/viewModels/dataLayerTableRowViewModel';
import { AiFindingsTableRowViewModel } from 'src/app/models/viewModels/aiFindingsTableRowViewModel';
import { FindingModel } from 'src/app/models/api/finding.model';
import { CombinedIntegration, IntegrationsScansResponse } from 'src/app/models/api/integration.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  public static isRefreshLabels = false;

  public integrations$ = new ReplaySubject<CombinedIntegration[]>(1);
  public labels$ = new ReplaySubject<LabelModel[]>(1);
  public accountMinimalPolicies$ = new ReplaySubject<PolicyMinimalModel[]>(1);
  public baseViolationRule$ = new ReplaySubject<BaseViolationRule[]>(1);

  private readonly _violations$ = new BehaviorSubject<IViolationsViewModel[]>([]);
  private readonly _hygiene$ = new BehaviorSubject<IViolationsViewModel[]>([]);
  private readonly _findings$ = new BehaviorSubject<AiFindingsTableRowViewModel[]>([]);
  private readonly _selectedViolation$ = new BehaviorSubject<ViolationModel | null>(null);
  private readonly _selectedFinding$ = new BehaviorSubject<FindingModel | null>(null);
  private readonly _selectedDataLensItem$ = new BehaviorSubject<IDataLayerTableRowViewModel | null>(null);

  /**
   * This is a map of integration id to integration name, this will be updated each time the integrations are updated
   */
  public integrationsNamesById: { [id: string]: string } = {};

  private _amountOfIntegrations = -1;
  private _effectivePolicySettingsStore: EffectivePolicySettingsStore;

  constructor(
    private readonly _integrationHttpService: IntegrationsHttpService,
    private readonly _violationRulesHttpService: ViolationRulesHttpService,
    private readonly _policyHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _labelsHttpService: LabelsHttpService,
    private readonly _logger: LoggerService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.updateIntegrations$().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    this.refreshLabels();
    this._getDefaultViolationRules();
  }

  public refreshLabels() {
    if (StoreService.isRefreshLabels) return;
    StoreService.isRefreshLabels = true;
    const minutes = 5;
    timer(0, minutes * 60 * 1000)
      .pipe(
        tap(() => this.updateLabels().subscribe()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public updateLabels(): Observable<void> {
    return this._labelsHttpService.getLabels().pipe(
      tap((jsonHalLabels: JsonHalCollection<LabelModel>) => {
        this.labels$.next(jsonHalLabels._embedded.items);
      }),
      map(() => undefined),
      catchError((err) => {
        this._httpErrorHandlerService.handle(err);
        this.labels$.next([]);
        return of(undefined);
      })
    );
  }

  public updateIntegrations$(): Observable<CombinedIntegration[]> {
    return forkJoin([
      this._integrationHttpService.getIntegrationsDetails$(),
      this._integrationHttpService.getIntegrationsScans$(),
    ]).pipe(
      map(([integrationsDetails, integrationsScansResponse]) =>
        this._combineIntegrationsDetailsAndScansData(integrationsDetails, integrationsScansResponse)
      ),
      map((combinedIntegrations) => this._sortIntegrations(combinedIntegrations)),
      tap((sortedIntegrations: CombinedIntegration[]) => {
        if (this._amountOfIntegrations === -1 || sortedIntegrations.length !== this._amountOfIntegrations) {
          this.updateAccountMinimalPolicies();
        }

        this.integrationsNamesById = sortedIntegrations.reduce(
          (map, integration: CombinedIntegration) => ({
            ...map,
            [integration.id]: integration.name,
          }),
          {}
        );

        this._amountOfIntegrations = sortedIntegrations.length;
        this.integrations$.next(sortedIntegrations);

        return sortedIntegrations;
      }),
      catchError((error) => {
        this._httpErrorHandlerService.handle(error);
        this.integrations$.next([]);

        return EMPTY;
      })
    );
  }

  public updateAccountMinimalPolicies() {
    this._policyHttpService.getPolicies().subscribe({
      next: (policies: PolicyMinimalModel[]) => {
        this.accountMinimalPolicies$.next(policies);
      },
      error: (err) => {
        this._httpErrorHandlerService.handle(err);
        this.accountMinimalPolicies$.next([]);
      },
    });
  }

  public getCategoryRulesByPlatformAndSettings(
    platform: Platform,
    effectivePolicySetting: EffectivePolicySettingEnum
  ): CategoryToRules[] {
    return (
      this._effectivePolicySettingsStore?.getCategoryRulesByPlatformAndSettings(platform, effectivePolicySetting) ?? []
    );
  }

  public getViolationsPageData$(): Observable<IViolationsViewModel[]> {
    return this._violations$.asObservable();
  }

  // Remove this method after refactoring ViolationsComponent and PrimeTableComponent so the latter does not accept BehSubject anymore
  public getViolationsPageDataAsSubject$(): BehaviorSubject<IViolationsViewModel[]> {
    return this._violations$;
  }

  public setViolationsPageData(violations: IViolationsViewModel[]): void {
    this._violations$.next(violations);
  }

  public getSelectedViolation$(): Observable<ViolationModel | null> {
    return this._selectedViolation$.asObservable();
  }

  public setSelectedViolation(violation: ViolationModel | null): void {
    this._selectedViolation$.next(violation);
  }

  public getSelectedDataLensItem$(): Observable<IDataLayerTableRowViewModel | null> {
    return this._selectedDataLensItem$.asObservable();
  }

  public setSelectedDataLensItem(dataLensItem: IDataLayerTableRowViewModel | null): void {
    this._selectedDataLensItem$.next(dataLensItem);
  }

  public getHygienePageData$(): Observable<IViolationsViewModel[]> {
    return this._hygiene$.asObservable();
  }

  // Remove this method after refactoring HygieneComponent and PrimeTableComponent so the latter does not accept BehSubject anymore
  public getHygienePageDataAsSubject$(): BehaviorSubject<IViolationsViewModel[]> {
    return this._hygiene$;
  }

  public setHygienePageData(hygienePageData: IViolationsViewModel[]): void {
    this._hygiene$.next(hygienePageData);
  }

  public getFindingsPageData$(): Observable<AiFindingsTableRowViewModel[]> {
    return this._findings$.asObservable();
  }

  public getFindingsPageDataAsSubject$(): BehaviorSubject<AiFindingsTableRowViewModel[]> {
    return this._findings$;
  }

  public setFindingsPageData(findingsPageData: AiFindingsTableRowViewModel[]): void {
    this._findings$.next(structuredClone(findingsPageData));
  }

  public getSelectedFinding$(): Observable<FindingModel | null> {
    return this._selectedFinding$.asObservable();
  }

  public setSelectedFinding(finding: FindingModel | null): void {
    this._selectedFinding$.next(finding);
  }

  public updatePageData(
    getData$: () => Observable<IViolationsViewModel[]>,
    setData: (data: IViolationsViewModel[]) => void,
    violationToUpdate: ViolationModel
  ): Observable<IViolationsViewModel[]> {
    return getData$().pipe(
      take(1),
      map((violations) =>
        violations.map((v) => (v.id === violationToUpdate.id ? { ...v, labels: violationToUpdate.labelBindings } : v))
      ),
      tap((updatedViolations) => setData(structuredClone(updatedViolations)))
    );
  }

  public updateFindingsPageData(
    getData$: () => Observable<AiFindingsTableRowViewModel[]>,
    setData: (data: AiFindingsTableRowViewModel[]) => void,
    findingToUpdate: FindingModel
  ): Observable<AiFindingsTableRowViewModel[]> {
    return getData$().pipe(
      take(1),
      map((findings) =>
        findings.map((f) => (f.id === findingToUpdate.id ? { ...f, labels: findingToUpdate.labelBindings || [] } : f))
      ),
      tap((updatedFindings) => setData(structuredClone(updatedFindings)))
    );
  }

  private _getDefaultViolationRules() {
    this._violationRulesHttpService.getBaseRules().subscribe((defaultRules) => {
      this.baseViolationRule$.next(defaultRules);
      this._effectivePolicySettingsStore = new EffectivePolicySettingsStore(defaultRules, this._logger);
    });
  }

  // Sort integrations alphabetically by name
  private _sortIntegrations(integrations: CombinedIntegration[]) {
    return integrations.sort((a, b) => a.name.localeCompare(b.name));
  }

  private _combineIntegrationsDetailsAndScansData(
    integrationsDetails: IntegrationModel[],
    integrationsScansResponse: IntegrationsScansResponse
  ): CombinedIntegration[] {
    const combinedIntegrations: CombinedIntegration[] = [];

    integrationsDetails.map((integration) => {
      const scanInfo = integrationsScansResponse.integrationsScans?.find(
        (scan) => scan.integrationId === integration.id
      );

      const combined = {
        ...integration,
        lastScanEndedAt: scanInfo?.scan?.endTime,
        dataCollectionStartedAt: scanInfo?.dataCollection?.startTime,
      };

      combinedIntegrations.push(combined);
    });

    return combinedIntegrations;
  }
}
